/**
 * PixelButton 컴포넌트
 * 레트로 게임 스타일의 픽셀 버튼
 * - 3D 픽셀 그림자 효과
 * - 클릭 시 눌림 애니메이션
 * - 게임보이 흑백 스타일
 */

import React, { ButtonHTMLAttributes } from 'react';

/**
 * PixelButton Props
 */
interface PixelButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * 버튼 크기
   * - small: 작은 버튼 (py-2 text-sm)
   * - medium: 중간 버튼 (py-3 text-base)
   * - large: 큰 버튼 (py-4 text-xl)
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * 버튼 스타일 변형
   * - primary: 주요 버튼 (어두운 회색, 강조)
   * - secondary: 보조 버튼 (중간 회색)
   * - screen: LCD 스크린 스타일 (게임보이 그린)
   */
  variant?: 'primary' | 'secondary' | 'screen';

  /**
   * 전체 너비 버튼 여부
   */
  fullWidth?: boolean;

  /**
   * 아이콘 (선택사항)
   */
  icon?: React.ReactNode;
}

/**
 * PixelButton 컴포넌트
 * 레트로 게임 스타일의 도트 버튼
 */
export const PixelButton: React.FC<PixelButtonProps> = ({
  size = 'medium',
  variant = 'primary',
  fullWidth = false,
  icon,
  children,
  className = '',
  ...props
}) => {
  // 사이즈별 클래스
  const sizeClasses = {
    small: 'py-2 px-4 text-sm',
    medium: 'py-3 px-6 text-base',
    large: 'py-4 px-8 text-xl',
  };

  // 변형별 스타일
  const variantStyles = {
    primary: {
      className: 'bg-gray-700 hover:bg-gray-600 text-white',
      style: {
        border: '4px solid #4b5563',
        boxShadow: '0 6px 0 #374151, 0 8px 10px rgba(0,0,0,0.3)',
        textShadow: '2px 2px 0 #000',
      },
    },
    secondary: {
      className: 'bg-gray-800 hover:bg-gray-700 text-gray-300',
      style: {
        border: '3px solid #4b5563',
        boxShadow: '0 4px 0 #374151',
        textShadow: '1px 1px 0 #000',
      },
    },
    screen: {
      className: 'bg-[#0f380f] hover:bg-[#306230] text-[#9bbc0f]',
      style: {
        border: '3px solid #306230',
        boxShadow: '0 4px 0 #306230, inset 0 0 10px rgba(15,56,15,0.5)',
      },
    },
  };

  const variantStyle = variantStyles[variant];

  return (
    <button
      className={`
        font-pixel
        transition-all
        active:translate-y-1
        ${sizeClasses[size]}
        ${variantStyle.className}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      style={variantStyle.style}
      {...props}
    >
      <span className="inline-flex items-center gap-2">
        {icon && <span>{icon}</span>}
        <span>{children}</span>
      </span>
    </button>
  );
};
